import { Scene, Vector3 } from '@babylonjs/core';
import { ChunkManager } from '../voxel/ChunkManager';

export interface WorldConfig {
  name: string;
  seed?: number;
  renderDistance?: number;
}

export class World {
  private scene: Scene;
  private config: Required<WorldConfig>;
  private chunkManager: ChunkManager;
  private spawnPoint: Vector3 = new Vector3(0, 20, 0);
  private playerPosition: Vector3 = new Vector3(0, 20, 0);

  constructor(scene: Scene, config: WorldConfig) {
    this.scene = scene;
    this.config = {
      name: config.name,
      seed: config.seed ?? Math.floor(Math.random() * 1000000),
      renderDistance: config.renderDistance ?? 4,
    };

    this.chunkManager = new ChunkManager(scene, {
      renderDistance: this.config.renderDistance,
      terrainConfig: {
        seed: this.config.seed,
      },
    });
  }

  public initialize(): void {
    // Calculate spawn point based on terrain height
    const spawnX = 8;
    const spawnZ = 8;
    const height = this.chunkManager.getHeightAt(spawnX, spawnZ);
    this.spawnPoint = new Vector3(spawnX, height + 2, spawnZ);
    this.playerPosition = this.spawnPoint.clone();

    // Initialize chunk manager
    this.chunkManager.initialize(this.playerPosition);
  }

  public getName(): string {
    return this.config.name;
  }

  public getSeed(): number {
    return this.config.seed;
  }

  public getSpawnPoint(): Vector3 {
    return this.spawnPoint.clone();
  }

  public setSpawnPoint(position: Vector3): void {
    this.spawnPoint = position.clone();
  }

  public getRenderDistance(): number {
    return this.config.renderDistance;
  }

  public setRenderDistance(distance: number): void {
    this.config.renderDistance = Math.max(1, Math.min(16, distance));
    this.chunkManager.setRenderDistance(this.config.renderDistance);
  }

  public updatePlayerPosition(position: Vector3): void {
    this.playerPosition = position.clone();
    this.chunkManager.updateCenter(this.playerPosition);
  }

  public update(_deltaTime: number): void {
    this.chunkManager.update();
  }

  public getBlock(x: number, y: number, z: number): number {
    return this.chunkManager.getBlock(Math.floor(x), Math.floor(y), Math.floor(z));
  }

  public setBlock(x: number, y: number, z: number, blockId: number): boolean {
    return this.chunkManager.setBlock(Math.floor(x), Math.floor(y), Math.floor(z), blockId);
  }

  public getHeightAt(x: number, z: number): number {
    return this.chunkManager.getHeightAt(Math.floor(x), Math.floor(z));
  }

  public getChunkManager(): ChunkManager {
    return this.chunkManager;
  }

  public getScene(): Scene {
    return this.scene;
  }

  public getStats(): { loadedChunks: number; queuedChunks: number } {
    return {
      loadedChunks: this.chunkManager.getLoadedChunkCount(),
      queuedChunks: this.chunkManager.getQueuedChunkCount(),
    };
  }

  public dispose(): void {
    this.chunkManager.dispose();
  }
}
